import { api } from "../api";
import { getAnalyticsDateRange } from "./getAnalyticsDateRange";

/**
 * Fetch all campaign analytics stats from Gadget views with streaming updates.
 * Each metric is displayed as soon as it's available.
 * Uses views with campaignId parameter.
 *
 * @param {Object} params
 * @param {string} params.customerId - The customer ID
 * @param {string} params.shopId - The shop ID (for turnover/sales)
 * @param {string} params.campaignId - The campaign ID
 * @param {string} params.selectedDateFilter - The selected date filter (Today, ThisWeek, etc.)
 * @param {Date} params.startDate - Custom start date for "Between" filter
 * @param {Date} params.endDate - Custom end date for "Between" filter
 * @param {Function} params.onUpdate - Callback called with partial stats as they arrive
 * @returns {Promise<void>} Resolves when all metrics are fetched
 */
export const fetchCampaignAnalyticsStats = async ({
  customerId,
  shopId,
  campaignId,
  selectedDateFilter,
  startDate,
  endDate,
  onUpdate,
}) => {
  const { from, to } = getAnalyticsDateRange(
    selectedDateFilter,
    startDate,
    endDate,
  );

  const commonParams = { customerId, campaignId, from, to };
  const shopParams = { shopId, campaignId, from, to };
  const suffix = selectedDateFilter;

  // Define all metrics to fetch (using ByCampaignId views)
  const metricsFetchers = [
    {
      fetch: () => api.getAnalyticsSentByCampaignId(commonParams),
      transform: (result) => ({
        [`sent${suffix}`]: result?.messages?.sent ?? 0,
      }),
    },
    {
      fetch: () => api.getAnalyticsDeliveredByCampaignId(commonParams),
      transform: (result) => ({
        [`delivered${suffix}`]: result?.messages?.delivered ?? 0,
      }),
    },
    {
      fetch: () => api.getAnalyticsReadByCampaignId(commonParams),
      transform: (result) => ({
        [`read${suffix}`]: result?.messages?.read ?? 0,
      }),
    },
    {
      fetch: () => api.getAnalyticsMessagesCountByCampaignId(commonParams),
      transform: (result) => ({
        [`messagesCount${suffix}`]: result?.messages?.messagesCount ?? 0,
      }),
    },
    {
      fetch: () => api.getAnalyticsWhatsappCostByCampaignId(commonParams),
      transform: (result) => ({
        [`totalWhatsappCostInEuros${suffix}`]:
          result?.messages?.totalWhatsappCostInEuros ?? 0,
        totalWhatsappCostInEuros:
          result?.messages?.totalWhatsappCostInEuros ?? 0,
      }),
    },
    {
      fetch: () =>
        shopId
          ? api.getAnalyticsTurnoverByCampaignId(shopParams)
          : Promise.resolve(null),
      transform: (result) => ({
        [`turnover${suffix}`]: result?.shopifyOrders?.turnover ?? 0,
      }),
    },
    {
      fetch: () => api.getAnalyticsExternalTurnoverByCampaignId(commonParams),
      transform: (result) => ({
        [`externalTurnover${suffix}`]: result?.messages?.externalTurnover ?? 0,
      }),
    },
    {
      fetch: () =>
        shopId
          ? api.getAnalyticsSalesByCampaignId(shopParams)
          : Promise.resolve(null),
      transform: (result) => ({
        [`sales${suffix}`]: result?.shopifyOrders?.sales ?? 0,
      }),
    },
  ];

  // Launch all fetches in parallel, update state as each completes
  const promises = metricsFetchers.map(async ({ fetch, transform }) => {
    try {
      const result = await fetch();
      const partialStats = transform(result);
      onUpdate((prev) => ({ ...prev, ...partialStats }));
    } catch (error) {
      console.error("Error fetching campaign metric:", error);
    }
  });

  await Promise.all(promises);
};
